import React from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';

import i18n from '../../i18n';

/** Binary units, so a 5 MiB limit reads "5 MB" and a file just over it more. */
export const formatFileSize = (bytes) =>
    bytes < 1024 * 1024
        ? `${Math.max(1, Math.round(bytes / 1024))} KB`
        : `${Number((bytes / (1024 * 1024)).toFixed(1))} MB`;

/**
 * Why `file` cannot be taken, or null. `accept` is the input's own list
 * ("image/png,application/pdf", "image/*"): the browser uses it only as the
 * picker's default filter, so anything can still be chosen.
 */
export const fileRefusal = (file, { accept, maxBytes } = {}) => {
    const types = (accept || '')
        .split(',')
        .map((type) => type.trim())
        .filter(Boolean);
    const type = file.type || '';
    const accepted =
        !types.length ||
        types.some((t) => (t.endsWith('/*') ? type.startsWith(t.slice(0, -1)) : t === type));
    if (!accepted) {
        return i18n.t('errors.fileTypeNotAllowed', { name: file.name });
    }
    if (maxBytes && file.size > maxBytes) {
        return i18n.t('errors.fileTooLarge', {
            name: file.name,
            size: formatFileSize(file.size),
            max: formatFileSize(maxBytes)
        });
    }
    return null;
};

/**
 * A file picked from disk and held, not uploaded: the value is the File itself and
 * the caller decides what to send — the payout confirm reads it into its own JSON.
 * Upload, by contrast, posts to /api/files the moment one is chosen.
 *
 * Head keys: `accept`, checked here too, and `maxBytes`. A refused file is never
 * handed on; the field says why instead.
 */
export default class DialogFileField extends React.Component {
    state = { refusal: null };

    handleChoose = (event) => {
        const file = event.target.files?.[0];
        // Cleared so that choosing the same file again, after removing it, fires.
        event.target.value = '';
        if (!file) return;
        const refusal = fileRefusal(file, this.props.field);
        this.setState({ refusal });
        this.props.onChange(this.props.field.id, refusal ? null : file);
    };

    handleClear = () => {
        this.setState({ refusal: null });
        this.props.onChange(this.props.field.id, null);
    };

    render() {
        const { field, value } = this.props;
        const disabled = Boolean(this.props.disabled || field.onlyRead);
        // Only a real file counts: CreateTransactionDialog seeds '', and its
        // "previous data" cache would bring a File back as {}.
        const file = value instanceof Blob ? value : null;
        const error = this.state.refusal || this.props.error;

        return (
            <Box>
                <Typography variant="subtitle2" gutterBottom>
                    {i18n.t(`columns.${field.label}`) + (field.required ? ' *' : '')}
                </Typography>

                {file ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AttachFileIcon fontSize="small" color="action" />
                        <Typography variant="body2" noWrap sx={{ minWidth: 0 }} title={file.name}>
                            {file.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatFileSize(file.size)}
                        </Typography>
                        <IconButton
                            size="small"
                            aria-label={i18n.t('common.delete')}
                            onClick={this.handleClear}
                            disabled={disabled}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                ) : (
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        disabled={disabled}
                    >
                        {i18n.t('buttons.chooseFile')}
                        <input
                            type="file"
                            hidden
                            accept={field.accept}
                            onChange={this.handleChoose}
                        />
                    </Button>
                )}

                {(error || field.helperText) && (
                    <Typography
                        variant="caption"
                        component="div"
                        color={error ? 'error' : 'text.secondary'}
                        sx={{ mt: 0.5 }}
                    >
                        {error || i18n.t(`helperTexts.${field.helperText}`)}
                    </Typography>
                )}
            </Box>
        );
    }
}
